import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, MapPin, Phone, Mail, Share2, Award, ArrowLeft, Play, Pause, Volume2, Loader2, UserPlus, MessageSquare, Check, Quote, HeartHandshake } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { getResume, ttsSpeak, requestConnection, listConnections } from "@/lib/api";
import { SkillTag, CredentialBadge, categoryIcon } from "@/components/shared";
import { CommunityIcon, accentClasses } from "@/lib/communityIcons";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const buildResumeScript = (data, verified) => {
  const parts = [`${data.name}.`, data.headline ? `${data.headline}.` : ""];
  if (data.location) parts.push(`Based in ${data.location}.`);
  if (data.bio) parts.push(data.bio);
  if (verified.length) {
    const skillLine = verified
      .map((s) => `${s.skill_name}, level ${s.proficiency_level} of 5`)
      .join("; ");
    parts.push(`Verified skills: ${skillLine}.`);
  }
  return parts.filter(Boolean).join(" ");
};

export default function Resume() {
  const { workerId } = useParams();
  const { currentUser } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [connection, setConnection] = useState(null); // {status, id, direction} or null

  const audioRef = useRef(null);
  const audioUrlRef = useRef(null);
  const [audioState, setAudioState] = useState("idle"); // idle | loading | playing | paused

  useEffect(() => {
    getResume(workerId).then(setData).catch(() => setNotFound(true));
  }, [workerId]);

  useEffect(() => {
    if (!currentUser || currentUser.id === workerId) return;
    listConnections().then((cs) => {
      const c = cs.find((x) => x.other.id === workerId);
      setConnection(c || null);
    });
  }, [currentUser, workerId]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
    };
  }, []);

  const share = () => {
    navigator.clipboard?.writeText(window.location.href);
    toast.success("Resume link copied to clipboard!");
  };

  const playResume = async () => {
    if (!data) return;
    if (audioState === "playing" && audioRef.current) {
      audioRef.current.pause();
      return;
    }
    if (audioState === "paused" && audioRef.current) {
      audioRef.current.play();
      return;
    }
    try {
      setAudioState("loading");
      const verified = data.skills.filter((s) => s.verified_status === "verified");
      const script = buildResumeScript(data, verified);
      const url = await ttsSpeak(script);
      audioUrlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onplay = () => setAudioState("playing");
      audio.onpause = () => setAudioState((s) => (s === "playing" ? "paused" : s));
      audio.onended = () => {
        setAudioState("idle");
        if (audioUrlRef.current) {
          URL.revokeObjectURL(audioUrlRef.current);
          audioUrlRef.current = null;
        }
        audioRef.current = null;
      };
      audio.onerror = () => {
        setAudioState("idle");
        toast.error("Playback failed");
      };
      await audio.play();
    } catch (e) {
      setAudioState("idle");
      toast.error("Voice playback failed");
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <h1 className="font-heading text-2xl font-bold">Resume not found</h1>
        <Button className="mt-4" onClick={() => navigate("/")}>Go home</Button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-4">
        <Skeleton className="h-48 rounded-xl" />
        <Skeleton className="h-40 rounded-xl" />
      </div>
    );
  }

  const verified = data.skills.filter((s) => s.verified_status === "verified");
  const byCategory = verified.reduce((acc, s) => {
    (acc[s.category] = acc[s.category] || []).push(s);
    return acc;
  }, {});
  const isOwner = currentUser && currentUser.id === data.id;

  const connect = async () => {
    try {
      const r = await requestConnection(workerId);
      if (r.created) toast.success("Connection request sent");
      else toast.info(r.status === "accepted" ? "Already connected" : "Request already pending");
      const cs = await listConnections();
      setConnection(cs.find((x) => x.other.id === workerId) || null);
    } catch { toast.error("Could not send request"); }
  };

  const playIcon =
    audioState === "loading" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> :
    audioState === "playing" ? <Pause className="w-4 h-4 mr-1.5" /> :
    <Play className="w-4 h-4 mr-1.5" />;
  const playLabel =
    audioState === "loading" ? "Loading…" :
    audioState === "playing" ? "Pause" :
    audioState === "paused" ? "Resume" :
    "Play resume";

  return (
    <div className="min-h-screen grain">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <button
            data-testid="resume-back-btn"
            onClick={() => navigate(isOwner ? "/worker" : (currentUser?.role === "employer" ? "/employer" : "/"))}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex gap-2 flex-wrap">
            {currentUser && currentUser.id !== workerId && (
              connection?.status === "accepted" ? (
                <Button data-testid="resume-message-btn" variant="outline" size="sm" onClick={() => navigate(`/messages/${workerId}`)}>
                  <MessageSquare className="w-4 h-4 mr-1.5" /> Message
                </Button>
              ) : connection?.status === "pending" ? (
                <Button variant="outline" size="sm" disabled>Pending</Button>
              ) : (
                <Button data-testid="resume-connect-btn" variant="outline" size="sm" onClick={connect}>
                  <UserPlus className="w-4 h-4 mr-1.5" /> Connect
                </Button>
              )
            )}
            <Button
              data-testid="resume-play-btn"
              variant="outline"
              size="sm"
              onClick={playResume}
              disabled={audioState === "loading"}
              aria-label={playLabel}
            >
              {playIcon}
              <span className="hidden sm:inline">{playLabel}</span>
              <span className="sm:hidden"><Volume2 className="w-4 h-4" /></span>
            </Button>
            <Button data-testid="resume-share-btn" variant="outline" size="sm" onClick={share}>
              <Share2 className="w-4 h-4 mr-1.5" /> Share
            </Button>
          </div>
        </div>

        {/* Header card */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-border bg-card p-6 sm:p-8"
          data-testid="resume-header"
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            <Avatar className="w-24 h-24 border-2 border-primary/20">
              <AvatarImage src={data.avatar_url} className="object-cover" />
              <AvatarFallback>{data.name[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-bold">
                <ShieldCheck className="w-3.5 h-3.5" /> SkillBridge Verified
              </div>
              <h1 className="mt-2 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">{data.name}</h1>
              <p className="text-base text-foreground/80">{data.headline}</p>
              <div className="mt-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
                {data.location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" />{data.location}</span>}
                {data.phone && <span className="inline-flex items-center gap-1.5"><Phone className="w-4 h-4" />{data.phone}</span>}
                {data.email && <span className="inline-flex items-center gap-1.5"><Mail className="w-4 h-4" />{data.email}</span>}
              </div>
            </div>
          </div>
          {data.bio && <p className="mt-6 text-base leading-relaxed text-foreground/80 border-t border-border pt-6">{data.bio}</p>}
          {audioState === "playing" && (
            <div data-testid="resume-playing-indicator" className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-bold">
              <Volume2 className="w-3.5 h-3.5" /> Reading resume aloud…
            </div>
          )}
        </motion.div>

        {/* Badges */}
        {data.badges.length > 0 && (
          <section className="mt-6 rounded-xl border border-border bg-card p-6 sm:p-8">
            <h2 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-5">Credential Badges</h2>
            <div className="flex flex-wrap gap-5">
              {data.badges.map((b, i) => <CredentialBadge key={b.id} badge={b} index={i} />)}
            </div>
          </section>
        )}

        {/* Skills by category */}
        <section className="mt-6 rounded-xl border border-border bg-card p-6 sm:p-8">
          <h2 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-5">Verified Skills</h2>
          <div className="space-y-6">
            {Object.entries(byCategory).map(([cat, skills]) => (
              <div key={cat}>
                <div className="flex items-center gap-2 text-sm font-bold text-primary mb-3">
                  {categoryIcon(cat)} {cat}
                </div>
                <div className="flex flex-wrap gap-2.5">
                  {skills.map((s) => <SkillTag key={s.id} skill={s} testid={`resume-skill-${s.id}`} />)}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Assessment logs */}
        {data.assessments?.length > 0 && (
          <section className="mt-6 rounded-xl border border-border bg-card p-6 sm:p-8">
            <h2 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-5">AI Assessment Summaries</h2>
            <div className="space-y-4">
              {data.assessments.map((a) => (
                <div key={a.id} data-testid={`resume-assessment-${a.id}`} className="rounded-lg bg-accent p-5">
                  <div className="flex items-center gap-2 text-sm font-bold text-primary">{categoryIcon(a.category)} {a.category}</div>
                  <p className="mt-2 text-sm text-foreground/80">{a.ai_evaluation_summary}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Communities */}
        {data.communities?.length > 0 && (
          <section className="mt-6 rounded-xl border border-border bg-card p-6 sm:p-8" data-testid="resume-communities">
            <h2 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-4 flex items-center gap-1.5">
              <HeartHandshake className="w-3.5 h-3.5" /> Communities
            </h2>
            <div className="flex flex-wrap gap-2">
              {data.communities.map((c) => {
                const ac = accentClasses(c.accent);
                return (
                  <button
                    key={c.slug}
                    data-testid={`resume-community-${c.slug}`}
                    onClick={() => navigate(`/communities/${c.slug}`)}
                    className={`inline-flex items-center gap-2 rounded-full border border-border ${ac.bg} ${ac.text} px-3 py-1.5 text-sm font-semibold hover:ring-2 ${ac.ring} transition-shadow`}
                  >
                    <CommunityIcon name={c.icon} className="w-4 h-4" />
                    {c.name}
                    {c.role === "ally" && <span className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-70">Ally</span>}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Recommendations */}
        {data.recommendations?.length > 0 && (
          <section className="mt-6 rounded-xl border border-border bg-card p-6 sm:p-8" data-testid="resume-recommendations">
            <h2 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-5">Recommendations</h2>
            <div className="space-y-4">
              {data.recommendations.map((r) => (
                <div key={r.id} data-testid={`resume-rec-${r.id}`} className="rounded-lg bg-accent p-5">
                  <Quote className="w-5 h-5 text-primary/50 mb-2" />
                  <p className="text-sm leading-relaxed text-foreground/90">"{r.text}"</p>
                  <div className="mt-3 flex items-center gap-3">
                    <img alt="" src={r.reviewer?.avatar_url} className="w-9 h-9 rounded-full object-cover border border-border" onError={(e)=>{e.currentTarget.style.display='none'}} />
                    <div>
                      <div className="text-sm font-bold">{r.reviewer?.name}</div>
                      <div className="text-xs text-muted-foreground">{r.relationship || r.reviewer?.headline || r.reviewer?.company}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="mt-8 text-center text-xs text-muted-foreground flex items-center justify-center gap-1.5">
          <Award className="w-3.5 h-3.5" /> Credentials issued & verified by SkillBridge
        </div>
      </div>
    </div>
  );
}
